import { guidFor } from '@ember/object/internals';
import Component from '@glimmer/component';
import type { WithBoundArgs } from '@glint/template';
import LoadingSpinnerPulse from 'garaje/components/ui/classic/loading-spinner-pulse';
import UiTable from 'garaje/components/ui/table';
import type { ColumnHeader } from 'garaje/components/ui/table';
import type GroupInvite from 'garaje/models/group-invite';
import type { RecordArray } from 'garaje/utils/type-utils';

import { createCurriedDatetimeCell } from './group-list/datetime-cell';
import type DatetimeCell from './group-list/datetime-cell';
import { createCurriedNameCell } from './group-list/name-cell';
import type NameCell from './group-list/name-cell';

interface InvitesTableGroupInviteListSignature {
  Args: {
    isLoading?: boolean;
    limit?: number;
    model?: RecordArray<GroupInvite> | GroupInvite[] | null;
  };
  Element: HTMLDivElement;
}

export default class InvitesTableGroupInviteList extends Component<InvitesTableGroupInviteListSignature> {
  scrollingElementId: string = `${guidFor(this)}-scrolling-list`;

  get columnHeaders(): ColumnHeader[] {
    return [
      {
        class: 'w-64 max-w-64',
        key: 'expectedArrivalTime',
        label: 'Date',
        sortable: false,
        componentOptions: {
          // @ts-expect-error: TS does not like matching class components to `ComponentLike`
          template: this.datetimeCell,
        },
      },
      {
        class: 'w-96 max-w-96',
        key: 'groupName',
        label: 'Group name',
        sortable: false,
        componentOptions: {
          // @ts-expect-error: TS does not like matching class components to `ComponentLike`
          template: this.nameCell,
        },
      },
      {
        class: 'w-96 max-w-96',
        key: 'hostName',
        label: 'Host',
        sortable: false,
      },
      // Extra column takes up remaining width to reduce content shifting
      {
        class: 'w-auto',
        key: '',
        label: '',
        sortable: false,
      },
    ];
  }

  get datetimeCell(): WithBoundArgs<typeof DatetimeCell, 'isLoading'> {
    return createCurriedDatetimeCell(this.args.isLoading);
  }

  get nameCell(): WithBoundArgs<typeof NameCell, 'isLoading'> {
    return createCurriedNameCell(this.args.isLoading);
  }

  get models(): GroupInvite[] {
    const { model = [], isLoading = false, limit = 25 } = this.args;

    if (isLoading) {
      const placeholders = Array.from({ length: limit }, (_, i) => ({ id: `placeholder-${i}` }));
      return placeholders as GroupInvite[];
    }

    return model as GroupInvite[];
  }

  <template>
    <div aria-label="Group Invites List" ...attributes data-test-group-invites-list>
      <div
        id={{this.scrollingElementId}}
        class="relative min-h-[24rem] max-h-[48rem] w-full border border-carbon-10 rounded-md overflow-auto overscroll-contain"
      >
        {{#if @isLoading}}
          <div class="z-50 absolute inset-0 bg-white/50" data-test-task-spinner>
            <LoadingSpinnerPulse />
          </div>
        {{/if}}
        <div class="w-fit min-w-full">
          {{#if this.models.length}}
            <UiTable
              class="table w-fit min-w-full text-sm border-spacing-0"
              @columnHeaders={{this.columnHeaders}}
              @data={{this.models}}
              @headerClass="h-10 text-carbon-50 font-bold align-middle bg-white border-b border-carbon-10 sticky top-0 z-10 truncate"
              data-test-group-invites-list-table
            />
          {{else}}
            <div class="text-center !my-8">
              <img src="/assets/images/empty-state/no-invites.png" alt="No group invites" class="empty-invites" />
              <h2 data-test-empty-invites-list-message class="mt-8 font-normal text-carbon-50">
                {{if @isLoading "Loading..." "No group invites"}}
              </h2>
            </div>
          {{/if}}
        </div>
      </div>
    </div>
  </template>
}
